import React from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import className from 'classnames';

import Link from 'components/Link';
import TypeIcon from 'components/TypeIcon';

import { getVerticalName } from 'lib/verticalType';

export const SpecialTypes = { supported: ['slideshow', 'video', 'shop'] };

function getPackageName(packageClass, isRailAdjacent) {
  const packageSuffix = 'pkg ';
  const packageName = packageClass.replace(packageSuffix, '');

  if (isRailAdjacent) {
    return packageName.concat('--rail-adjacent');
  }

  return packageName;
}

function opinionEyebrow(primaryVertical, url = '') {
  const isOpinion = primaryVertical === 'think';

  return isOpinion && (
    <h2 className="related-content__eyebrow">
      <Link to={url} className="related-content__eyebrow-link" data-test="related-content__eyebrow">
        { i18next.t('Opinion') }
      </Link>
    </h2>
  );
}

function headline(headlineText, url) {
  return (
    <h3 className="related-content__headline" data-test="related-content__headline">
      <Link to={url} className="related-content__headline-link" data-test="related-content__headline__link">
        {headlineText}
      </Link>
    </h3>
  );
}

function icon(type, primaryVertical, vertical) {
  const isOpinion = primaryVertical === 'think' && vertical !== 'think';

  const { supported: SupportedSpecialIcons } = SpecialTypes;
  const specializedIcon = SupportedSpecialIcons.includes(type);

  return (
    <TypeIcon
      type={specializedIcon ? type : 'related-article'}
      className={className(
        'related-content__icon',
        {
          'related-content__icon-gallery': type === 'slideshow',
          'related-content__icon-opinion': isOpinion,
        },
      )}
    />
  );
}

function RelatedContent(props, context) {
  const container = 'relatedContent';
  const { vertical } = context;
  const {
    isRailAdjacent, related, parentNoImage, pkgClassName,
  } = props;

  const formattedPkgName = pkgClassName && pkgClassName.includes('standardLeadPlus') ? 'pkg related-content__item-featured--standard-lead' : pkgClassName;

  // packages that support 4 stories
  const withinFeatured = ['pkg related-content__item-featured--one-up', 'pkg related-content__item-featured--standard-lead', 'pkg related-content__item-featured--straight-up'].includes(formattedPkgName);
  const isLeadSectionFront = formattedPkgName === 'pkg related-content__item--lead-section-front';
  const isThink = vertical === 'think';

  const featuredPackage = formattedPkgName && getPackageName(formattedPkgName, isRailAdjacent);

  return !!related.length && (
    <div
      className={className(
        container,
        'related-content',
        {
          'related-content__parent-no-img': parentNoImage,
        },
      )}
      data-test="related-content"
    >
      <ul className="related-content__list" data-test="related-content__list">
        {
          related.map(
            ({
              id,
              type,
              computedValues: {
                headline: headlineText,
                unibrow: {
                  url: {
                    primary: primaryUrl,
                  } = {},
                } = {},
                url,
              },
              item,
            }) => {
              const primaryVertical = getVerticalName({ item });

              return (
                <li
                  key={id}
                  className={className(
                    'related-content__item',
                    {
                      'related-content__item-featured': withinFeatured,
                      [featuredPackage]: withinFeatured || isLeadSectionFront,
                    },
                  )}
                  data-contentid={id}
                  data-test="related-content__list__item"
                >
                  { icon(type, primaryVertical, vertical) }
                  { !isThink && opinionEyebrow(primaryVertical, primaryUrl) }
                  { headline(headlineText, url, vertical) }
                </li>
              );
            },
          )
        }
      </ul>
    </div>
  );
}

RelatedContent.contextTypes = {
  vertical: PropTypes.string,
};

RelatedContent.propTypes = {
  isRailAdjacent: PropTypes.bool,
  pkgClassName: PropTypes.string,
  parentNoImage: PropTypes.bool,
  related: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
};

RelatedContent.defaultProps = {
  isRailAdjacent: false,
  pkgClassName: null,
  parentNoImage: false,
};

export default RelatedContent;
